import {
  type BasicQuestion,
  ComplexQuestionTypeToString,
} from "@repo/app-types/client/client";
import { Color } from "@repo/app-types/constants/colors";
import type {
  ComplexQuestion,
  ComplexQuestionInsert,
} from "@repo/app-types/entities/question";
import {
  isComplexProjectQuestion,
  isComplexProjectQuestionInsert,
  isValidComplexQuestion,
} from "@repo/app-types/helpers/utilities";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { iconButton } from "@repo/ui/Buttons/iconButton";
import {
  type DropdownItem,
  DropdownMenu,
} from "@repo/ui/Dropdown/dropdownMenu";
import { Size } from "@repo/ui/helpers/helpers";
import { Pill } from "@repo/ui/Pills/pill";
import { IconDotsVertical, IconExclamationCircle } from "@tabler/icons-react";
import type { FC } from "react";
import { Button, MenuTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";

interface QuestionsManagerCellProps {
  questionNumber: number;
  question: ComplexQuestion | ComplexQuestionInsert | BasicQuestion;
  onClick: () => void;
  onRemove: () => void;
  onClickMoveUp?: () => void;
  onClickMoveDown?: () => void;
  isSelected: boolean;
}

export const QuestionsManagerCell: FC<QuestionsManagerCellProps> = ({
  questionNumber,
  question,
  onClick,
  onRemove,
  onClickMoveUp,
  onClickMoveDown,
  isSelected,
}) => {
  const dropdownItems: DropdownItem[] = [];
  if (onClickMoveUp)
    dropdownItems.push({
      label: "Move question up",
      onClick: onClickMoveUp,
    });

  if (onClickMoveDown)
    dropdownItems.push({
      label: "Move question down",
      onClick: onClickMoveDown,
    });

  dropdownItems.push({
    label: "Remove question",
    onClick: onRemove,
  });

  // Check if there's an error with this question
  const isComplexQuestion =
    isComplexProjectQuestionInsert(question) ||
    isComplexProjectQuestion(question);
  const hasError = isComplexQuestion && !isValidComplexQuestion(question, true);

  return (
    <div
      className={twMerge(
        "group relative flex w-full cursor-pointer flex-row space-x-2 border-b border-gray-900/5 p-2",
        isSelected ? "bg-blue-100" : "hover:bg-blue-50",
      )}
      onClick={onClick}
    >
      <Pill label={questionNumber} color={Color.Gray} size={Size.SMALL} />
      <div className="w-full">
        <div
          className="text-sm"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {question.question}
        </div>
        {hasError ? (
          <div className="mt-1 flex flex-row items-center text-orange-500">
            <IconExclamationCircle className="mr-1 inline h-5 w-5" />
            <span>Invalid question</span>
          </div>
        ) : null}
        {isComplexQuestion ? (
          <div className="mt-2">
            <Pill
              label={ComplexQuestionTypeToString[question.type]}
              color={Color.Yellow}
              size={Size.SMALL}
            />
          </div>
        ) : undefined}
      </div>

      <div
        className="absolute top-1/2 right-3 z-10 ml-3 -translate-y-1/2 transform"
        onClick={(evt) => {
          evt.stopPropagation();
        }}
      >
        <MenuTrigger>
          <Button
            onClick={(evt) => {
              evt.stopPropagation();
            }}
            className={iconButton({
              variant: ButtonVariant.Secondary,
              className:
                "opacity-0 group-hover:opacity-100 pressed:opacity-100",
            })}
          >
            <IconDotsVertical className="h-4 w-4" />
          </Button>
          <DropdownMenu items={dropdownItems} />
        </MenuTrigger>
      </div>
    </div>
  );
};
